/**
 * AnnouncementDetailModal — 공지사항 상세 조회 모달
 *
 * 공지사항 목록에서 항목 클릭 시 표시되는 상세 보기 모달.
 * textarea로 입력된 본문의 줄바꿈(\n)을 보존하여 렌더링한다.
 */

import React, { useEffect } from 'react';
import './AnnouncementDetailModal.css';

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TYPE_LABELS = {
  info: '안내',
  warning: '주의',
  important: '중요',
  feature: '새 기능',
  update: '업데이트',
  notice: '안내',
  maintenance: '점검',
};

const TYPE_CLASSES = {
  info: 'type-info',
  warning: 'type-warning',
  important: 'type-important',
  feature: 'type-feature',
  update: 'type-update',
  notice: 'type-notice',
  maintenance: 'type-maintenance',
};

export function AnnouncementDetailModal({ announcement, isOpen, onClose }) {
  // Hooks는 항상 동일한 순서로 호출 (Rules of Hooks 준수)
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // 조건부 렌더링은 Hooks 이후에
  if (!isOpen || !announcement) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const typeLabel = TYPE_LABELS[announcement.type] || '안내';
  const typeClass = TYPE_CLASSES[announcement.type] || 'type-info';

  return (
    <div className="ann-detail__backdrop" onClick={handleBackdropClick}>
      <div className="ann-detail__modal" role="dialog" aria-modal="true">
        {/* Header bar */}
        <div className="ann-detail__header">
          <div className="ann-detail__badges">
            {announcement.isPinned && (
              <span className="ann-detail__badge ann-detail__badge--pinned">
                <PinIcon /> 고정
              </span>
            )}
            <span className={`ann-detail__badge ann-detail__badge--${typeClass}`}>{typeLabel}</span>
          </div>
          <button className="ann-detail__close" onClick={onClose} aria-label="닫기">
            <CloseIcon />
          </button>
        </div>

        {/* Title + meta */}
        <div className="ann-detail__title-area">
          <h2 className="ann-detail__title">{announcement.title}</h2>
          <div className="ann-detail__meta">
            <span>
              <CalendarIcon /> {formatDate(announcement.createdAt)}
            </span>
            {announcement.updatedAt && announcement.updatedAt !== announcement.createdAt && (
              <span>
                <EditIcon /> {formatDate(announcement.updatedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Content body — white-space: pre-wrap으로 줄바꿈(0x0A) 보존 */}
        <div className="ann-detail__body">
          {announcement.content ? (
            <div className="ann-detail__content">{announcement.content}</div>
          ) : (
            <p className="ann-detail__empty">내용이 없습니다.</p>
          )}
        </div>

        {/* Footer */}
        <div className="ann-detail__footer">
          <button className="ann-detail__btn" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Icons ────────────────────────────────────────

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="12"
    height="12"
  >
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="14"
    height="14"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="14"
    height="14"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
    />
  </svg>
);

export default AnnouncementDetailModal;
